import { createFileRoute } from "@tanstack/react-router";
import { NetworkCallout } from "@/components/site/BrandNetwork";
import { ProductPage } from "@/components/site/ProductPage";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { ForeignNationalTools } from "@/components/site/ForeignNationalTools";
import { RentalStrategies } from "@/components/site/RentalStrategies";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { buildHead } from "@/lib/seo-head";

const SLUG = "foreign-national-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG);
const URL = `/${SLUG}`;

const KEYWORDS = [
  "foreign national loan",
  "foreign national mortgage",
  "no SSN mortgage",
  "loan for non-US citizen",
  "foreign national DSCR loan",
  "ITIN investment property loan",
  "US property loan for foreign investors",
  "foreign national rental property loan",
  "passport only mortgage",
  "non-resident real estate loan",
  "foreign national LLC loan",
  "international buyer mortgage USA",
].join(", ");

export const Route = createFileRoute("/foreign-national-loans/")({
  head: () =>
    buildHead({
      title: "Foreign National Loans for US Investment Property",
      description:
        "Buy US rental property without a Social Security Number, US credit, or US tax returns. Passport, entity, and reserves — that's the package.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: PRODUCT.shortName, url: URL },
      ],
      service: { name: PRODUCT.name },
      faqs: PRODUCT.faq.map((f) => ({ q: f.q, a: f.a })),
    }),

  component: () => (
    <ProductPage
      product={PRODUCT}
      topTools={
        <LiveDscrRateSection
          strategyCards
          scenarioOverrides={{ citizenship: "ForeignNational" }}
          toolLabel="Foreign national DSCR"
          id="live-rates"
          eyebrow="Live pricing · Foreign national"
          heading="Current Foreign National DSCR Rates"
          paragraphs={[
            "Foreign national pricing is not a single published number. Residency status, the credit documentation you can provide, entity vesting and reserve strength all change the eligible investor set, so a foreign national quote must be returned by the pricing engine for that exact scenario rather than read off a rate sheet.",
            "The live options below are DSCR quotes for a qualifying rental scenario and are shown so you can see the structure of a real quote — rate, the APR shown for that quote, points or lender credit, the primary lender fee and the monthly payment. Foreign national programs are separate programs with their own eligibility and pricing and may price differently.",
            "To see your actual pricing, run the scenario with foreign national selected in the rate center, or send passport, entity and reserve details and a loan officer will return written pricing.",
          ]}
          faqs={[
            { q: "Can you quote a foreign national rate without US credit?", a: "Yes. Pricing is driven by the property, leverage and documentation package rather than a US credit score, though the absence of US credit does affect which investors can price the file." },
            { q: "Is maximum leverage lower for foreign nationals?", a: "Maximum leverage on foreign national programs is set by each investor and can differ from standard DSCR limits. We confirm the limits that apply to your scenario in writing." },
          ]}
          links={[
            { to: "/investment-property-rates", label: "Price a foreign national DSCR scenario" },
            { to: "/dscr-loans", label: "How DSCR loans work" },
            { to: "/business-setup/itin", label: "ITIN and US entity setup for non-US investors" },
          ]}
          applyProduct="foreign-national"
        />
      }
      extraTools={
        <>
          <RentalStrategies />
          <ForeignNationalTools />
          <NetworkCallout
            title="Living in the US with an ITIN?"
            body="Foreign national DSCR loans are built for non-US residents. If you live here and file taxes with an ITIN, our sister site covers ITIN credit building and ITIN home financing."
            sites={["MyITINCredit"]}
          />
        </>
      }

    />
  ),
});
